/**
 * Machine Deck — Estate Console for claimed hardware only.
 * Soft≠Live: never invent IP / verified / skills. Activate leave-seat for Register.
 * Language matches ops-hud / cameras glass: chip mast + meters + roster + glass dock.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { html, stay, go, cmcPlainOf, hashSearchParams } from "./ui.js";
import { api, path, useApi } from "./api.js";
import { edgeDevicesOf } from "./edgeos-live.js";
import { isLocalHost } from "./pathseal.js";
import { confirmMachineRegister } from "./machine-register-gate.js";
import MachineQuickDrawer from "./machine-quick-drawer.js";
import { EdgeLeasePanel } from "./edge-lease-panel.js";
import { skillIndexOf, skillLabelOf } from "./machine-skill-labels.js";
import { softCapsGet, softCapsItems, isCapsRouteMissing } from "./machine-caps-soft-store.js";
import { ensureSoftEstate, softEstateHubRows } from "./machine-soft-estate.js";
import MachineJumpMenu from "./machine-jump-menu.js";
import MachineSkillFilter from "./machine-skill-filter.js";
import { stampGlyphOf } from "./machine-estate-controls.js";
import { boardHeadingOf, boardKindSay, boardLabelSay, looksLikeCode } from "./operator-say.js";

/** Contained rail scroll only — never scroll the page/sheet chrome. */
function scrollSelectedInRail() {
  const unit = document.querySelector('.mh-rail__unit[data-selected="1"]');
  if (!unit || typeof unit.scrollIntoView !== "function") return;
  const list = unit.closest(".mh-rail__list");
  try {
    // Pin page chrome: never let ancestors absorb a scrollIntoView.
    const sheet = unit.closest(".sheet.machine-deck") || document.querySelector(".sheet.machine-deck");
    const stage = unit.closest(".page__stage") || document.querySelector(".page__stage");
    const freeze = (el) => {
      if (!el) return;
      try { el.scrollTop = 0; el.scrollLeft = 0; } catch (_) { /* ignore */ }
    };
    freeze(sheet);
    freeze(stage);
    freeze(document.scrollingElement);
    freeze(document.documentElement);
    freeze(document.body);
    if (list && typeof list.scrollTop === "number") {
      const uTop = unit.offsetTop;
      const uBot = uTop + unit.offsetHeight;
      const viewTop = list.scrollTop;
      const viewBot = viewTop + list.clientHeight;
      if (uTop < viewTop) list.scrollTop = Math.max(0, uTop - 8);
      else if (uBot > viewBot) list.scrollTop = Math.max(0, uBot - list.clientHeight + 8);
      freeze(sheet);
      freeze(stage);
      return;
    }
    unit.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
    freeze(sheet);
    freeze(stage);
  } catch (_) { /* ignore */ }
}

function machineSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.device_paint_max);
  return {
    surveyAllowed: p.survey_plane_allowed === true,
    reReadAllowed: p.re_read_allowed != null ? !!p.re_read_allowed : true,
    devicePaintMax: Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 24,
  };
}

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__machine_status") return "/machine/status";
    if (op === "get__edge_status") return "/edge/status";
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const lim = q.limit != null ? "?limit=" + encodeURIComponent(String(q.limit)) : "";
      return "/devices" + lim;
    }
    if (op === "get__devices_capabilities") {
      const q = (opts && opts.query) || {};
      const parts = [];
      if (q.verified_only != null) parts.push("verified_only=" + encodeURIComponent(String(q.verified_only)));
      return "/devices/capabilities" + (parts.length ? "?" + parts.join("&") : "");
    }
    if (op === "get__devices_p_capabilities") {
      const id = encodeURIComponent(String((opts && opts.pathParams && opts.pathParams[0]) || ""));
      return "/devices/" + id + "/capabilities";
    }
    return "";
  }
}


/** Pure: extend device row with hub columns (honest Live fields only). */
export function machineHubRowOf(d) {
  const row = d && typeof d === "object" ? d : {};
  const raw = row.raw && typeof row.raw === "object" ? row.raw : {};
  const softPreview = raw.honesty === "soft_preview" || raw.soft_estate === true;
  const kind = String(
    row.kind || raw.board_profile || raw.product_hw_code || raw.hw_code_resolved
    || raw.chip_target || raw.kind || "",
  ).trim();
  const fw = String(row.fw || raw.fw || raw.fw_version || "").trim();
  const group = String(row.group || raw.notification_group || "").trim();
  /* Ontology: Live home Site = device.site_id only. Soft preview may paint soft_site_label. */
  const site = String(raw.site_id || "").trim()
    || (softPreview
      ? String(raw.soft_site_label || row.site || raw.site || "").trim()
      : "");
  const zone = String(row.zone || raw.zone || "").trim();
  let net = String(row.net || raw.net_type || raw.link || "").trim();
  if (net === "[object Object]") net = "";
  return {
    id: String(row.id || "").trim(),
    label: boardLabelSay({ label: row.label, display_label: raw.display_label, map_label: raw.map_label }, row.id),
    online: !!row.online,
    zone,
    group,
    site,
    kind,
    fw,
    hbAgeS: row.hbAgeS != null ? row.hbAgeS : null,
    net,
    raw,
  };
}

function skillTabFromHash() {
  return String(hashSearchParams().get("skill") || "").trim().toLowerCase();
}

/** Canon: /machine?view=health (ops-live / fleet health) puts Quiet units first. */
export function listFilterFromView(rows, view) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const v = String(view || "").trim().toLowerCase();
  if (v !== "health") return list;
  return list.sort((a, b) => Number(!!a.online) - Number(!!b.online)
    || String(a.label || "").localeCompare(String(b.label || "")));
}

function facetFromHash(key) {
  return String(hashSearchParams().get(key) || "").trim();
}

function facetTabsOf(devices, key) {
  const noun = key === "group" ? "group" : key === "site" ? "site" : key === "zone" ? "zone" : key;
  const counts = new Map();
  const meta = new Map();
  for (const d of devices || []) {
    const v = String((d && d[key]) || "").trim();
    if (!v) continue;
    counts.set(v, (counts.get(v) || 0) + 1);
    if (key === "zone") {
      const site = String((d && d.site) || "").trim();
      if (site && !meta.has(v)) meta.set(v, site);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([id, count]) => {
      const site = meta.get(id) || "";
      const unitSay = count + " unit" + (count === 1 ? "" : "s");
      return {
        id,
        label: id,
        count,
        say: site
          ? (unitSay + " · site " + site)
          : (unitSay + " · this " + noun),
      };
    });
}

function skillPreviewOf(ids, need) {
  /* Honesty: No skills yet */
  if (need) return "Nothing extra yet";
  const labels = (ids || []).slice(0, 3).map((id) => skillLabelOf(id));
  const extra = (ids || []).length - labels.length;
  return extra > 0 ? labels.join(" · ") + " +" + extra : labels.join(" · ");
}

/** Compact heartbeat age for roster — Live field only. */
function hbAgeSay(age) {
  const n = Number(age);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 90) return Math.round(n) + "s";
  if (n < 3600) return Math.round(n / 60) + "m";
  if (n < 86400) return Math.round(n / 3600) + "h";
  return Math.round(n / 86400) + "d";
}

function rosterTitleOf(d, caps, need) {
  const bits = [
    boardLabelSay(d, d.id),
    d.online ? "Online" : "Quiet",
    d.group && !looksLikeCode(d.group) ? d.group : "",
    boardKindSay(d.kind),
    d.fw ? ("Software " + String(d.fw).replace(/-preview$/i, "")) : "",
    skillPreviewOf(caps, need),
  ].filter(Boolean);
  return bits.join(" · ");
}

export default function MachineDeck({ toast }) {
  const local = isLocalHost();
  // Prefer /machine/status; on 404/error fall back to /edge/status Soft≠empty-list-on-missing-route.
  const machinePosture = useApi(safePath("get__machine_status"));
  const machineReady = !machinePosture.loading && machinePosture.data != null && !machinePosture.error;
  const edgePosture = useApi(
    (!machinePosture.loading && !machineReady) ? safePath("get__edge_status") : null,
  );
  const posture = machineReady ? machinePosture : edgePosture;
  const seat = machineSeatOf(
    (machinePosture.loading || (!machineReady && edgePosture.loading))
      ? { survey_plane_allowed: false, re_read_allowed: false, limits: { device_paint_max: 24 } }
      : posture.data,
  );
  const surveyOn = !!seat.surveyAllowed;
  const paintMax = seat.devicePaintMax || 24;
  const softEstate = local; // localhost preview paint: demo estate so layout can be judged

  const reg = useApi(
    (!softEstate && surveyOn)
      ? safePath("get__devices", { query: { limit: Math.min(Math.max(paintMax, 24), 500) } })
      : null,
  );
  const devices = useMemo(() => {
    if (softEstate) {
      ensureSoftEstate();
      return softEstateHubRows().slice(0, paintMax);
    }
    return edgeDevicesOf(reg.data).slice(0, paintMax).map(machineHubRowOf);
  }, [softEstate, reg.data, paintMax]);

  const [skillTab, setSkillTab] = useState(() => skillTabFromHash() || "all");
  const [viewTab, setViewTab] = useState(() => String(hashSearchParams().get("view") || "").trim());
  const [groupTab, setGroupTab] = useState(() => facetFromHash("group") || "all");
  const [siteTab, setSiteTab] = useState(() => facetFromHash("site") || "all");
  const [zoneTab, setZoneTab] = useState(() => facetFromHash("zone") || "all");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(() => String(hashSearchParams().get("device") || "").trim());
  const [capsById, setCapsById] = useState(() => new Map());
  const [capsState, setCapsState] = useState("idle"); // idle | load | ready | off | err

  useEffect(() => {
    const onHash = () => {
      setSkillTab(skillTabFromHash() || "all");
      setViewTab(String(hashSearchParams().get("view") || "").trim());
      setGroupTab(facetFromHash("group") || "all");
      setSiteTab(facetFromHash("site") || "all");
      setZoneTab(facetFromHash("zone") || "all");
      setSelectedId(String(hashSearchParams().get("device") || "").trim());
    };
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  const loadCaps = useCallback(async () => {
    if (!devices.length) {
      setCapsById(new Map());
      setCapsState("ready");
      return;
    }
    if (softEstate) {
      ensureSoftEstate();
      const map = new Map();
      for (const d of devices) {
        const soft = softCapsGet(d.id);
        map.set(d.id, soft || { claimed: [], verified: [], deviceType: "edge_ai_node", honesty: "soft_preview" });
      }
      setCapsById(map);
      setCapsState("ready");
      return;
    }
    if (!surveyOn) {
      setCapsById(new Map());
      setCapsState("ready");
      return;
    }
    setCapsState("load");
    const map = new Map();
    let liveMissing = false;

    try {
      const indexPath = safePath("get__devices_capabilities", { query: { verified_only: false } });
      if (indexPath) {
        const env = await api(indexPath);
        const items = (env && env.items) || [];
        if (Array.isArray(items) && items.length) {
          for (const row of items) {
            const did = String(row.device_id || "").trim();
            if (!did) continue;
            map.set(did, {
              claimed: Array.isArray(row.claimed) ? row.claimed.map(String) : [],
              verified: Array.isArray(row.verified) ? row.verified.map(String) : [],
              deviceType: String(row.device_type || "").trim(),
            });
          }
        }
      }
    } catch (e) {
      if (isCapsRouteMissing(e)) liveMissing = true;
    }

    const missing = devices.filter((d) => !map.has(d.id));
    if (missing.length) {
      await Promise.all(missing.map(async (d) => {
        try {
          const p = safePath("get__devices_p_capabilities", { pathParams: [d.id] });
          if (!p) return;
          const env = await api(p);
          const item = env && env.item;
          map.set(d.id, {
            claimed: item && Array.isArray(item.claimed) ? item.claimed.map(String) : [],
            verified: item && Array.isArray(item.verified) ? item.verified.map(String) : [],
            deviceType: item ? String(item.device_type || "").trim() : "",
            catalog: Array.isArray(env && env.catalog) ? env.catalog : [],
          });
        } catch (err) {
          if (isCapsRouteMissing(err)) liveMissing = true;
          const soft = softCapsGet(d.id);
          map.set(d.id, soft || { claimed: [], verified: [], deviceType: "" });
        }
      }));
    }

    if (liveMissing && local) {
      for (const row of softCapsItems()) {
        const did = String(row.device_id || "").trim();
        if (!did || map.has(did) && (map.get(did).claimed || []).length) continue;
        if (!map.has(did) || !(map.get(did).claimed || []).length) {
          map.set(did, {
            claimed: row.claimed,
            verified: row.verified,
            deviceType: row.device_type,
            honesty: "soft_preview",
          });
        }
      }
    }

    setCapsById(map);
    setCapsState(liveMissing && !local ? "off" : "ready");
  }, [surveyOn, devices, local, softEstate]);

  useEffect(() => { loadCaps(); }, [loadCaps]);

  const skillIndex = useMemo(() => skillIndexOf(capsById), [capsById]);

  const listed = useMemo(() => {
    let rows = devices.slice();
    if (skillTab && skillTab !== "all") {
      const ids = new Set(skillIndex.bySkill.get(skillTab) || []);
      rows = rows.filter((d) => ids.has(d.id));
    }
    if (groupTab && groupTab !== "all") {
      rows = rows.filter((d) => String(d.group || "").trim() === groupTab);
    }
    if (siteTab && siteTab !== "all") {
      rows = rows.filter((d) => String(d.site || "").trim() === siteTab);
    }
    if (zoneTab && zoneTab !== "all") {
      rows = rows.filter((d) => String(d.zone || "").trim() === zoneTab);
    }
    if (String(viewTab || "").toLowerCase() === "health") {
      rows = listFilterFromView(rows, "health");
    } else {
      rows = rows.slice().sort((a, b) => Number(b.online) - Number(a.online)
        || String(a.label).localeCompare(String(b.label)));
    }
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((d) => {
      const caps = (skillIndex.byDevice.get(d.id) || []).join(" ");
      return [d.label, d.id, d.group, d.zone, d.site, d.kind, caps]
        .some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [devices, skillTab, viewTab, groupTab, siteTab, zoneTab, query, skillIndex]);

  const selected = devices.find((d) => d.id === selectedId)
    || (selectedId ? {
      id: selectedId, label: selectedId, online: false, zone: "", group: "", site: "",
      kind: "", fw: "", hbAgeS: null, net: "", raw: {},
    } : null);

  const stayMachine = (next) => {
    const skill = next.skill != null ? next.skill : skillTab;
    const group = next.group != null ? next.group : groupTab;
    const site = next.site != null ? next.site : siteTab;
    const zone = next.zone != null ? next.zone : zoneTab;
    const device = next.device != null ? next.device : selectedId;
    const parts = [];
    if (skill && skill !== "all") parts.push("skill=" + encodeURIComponent(skill));
    if (group && group !== "all") parts.push("group=" + encodeURIComponent(group));
    if (site && site !== "all") parts.push("site=" + encodeURIComponent(site));
    if (zone && zone !== "all") parts.push("zone=" + encodeURIComponent(zone));
    if (device) parts.push("device=" + encodeURIComponent(device));
    stay("/machine" + (parts.length ? "?" + parts.join("&") : ""));
  };

  const setTab = (id) => {
    const next = String(id || "all").trim().toLowerCase() || "all";
    setSkillTab(next);
    stayMachine({ skill: next, device: selectedId });
  };

  const setGroupFacet = (id) => {
    const next = String(id || "all").trim() || "all";
    setGroupTab(next);
    stayMachine({ group: next, device: selectedId });
  };
  const setSiteFacet = (id) => {
    const next = String(id || "all").trim() || "all";
    setSiteTab(next);
    stayMachine({ site: next, device: selectedId });
  };
  const setZoneFacet = (id) => {
    const next = String(id || "all").trim() || "all";
    setZoneTab(next);
    stayMachine({ zone: next, device: selectedId });
  };

  const selectMachine = (id) => {
    const did = String(id || "").trim();
    setSelectedId(did);
    stayMachine({ skill: skillTab, device: did });
    // Sync freeze: stop page chrome from jumping before the effect runs.
    try {
      const sheet = document.querySelector(".sheet.machine-deck");
      const stage = document.querySelector(".page__stage");
      if (sheet) { sheet.scrollTop = 0; sheet.scrollLeft = 0; }
      if (stage) { stage.scrollTop = 0; stage.scrollLeft = 0; }
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    } catch (_) { /* ignore */ }
  };

  const fromRoute = skillTab && skillTab !== "all"
    ? "/machine?skill=" + encodeURIComponent(skillTab)
    : "/machine";

  const onCapsSaved = (did, row) => {
    setCapsById((prev) => {
      const next = new Map(prev);
      next.set(did, row);
      return next;
    });
  };

  useEffect(() => {
    if (!selectedId) return;
    scrollSelectedInRail();
  }, [selectedId, listed.length]);

  const emptySay = reg.loading
    ? "Reading the estate…"
    : !surveyOn
      ? "This seat cannot survey machines yet. The list stays empty."
      : !devices.length
        ? "No machines claimed on this account. Register adds a unit."
        : skillTab !== "all" && !listed.length
          ? "No unit claims “" + skillLabelOf(skillTab) + "” yet."
          : (groupTab !== "all" || siteTab !== "all" || zoneTab !== "all") && !listed.length
            ? "No machines match these Group / Site / Zone filters."
          : "No machines match this search.";

  const muxKind = skillTab === "all" ? "estate" : "attention";
  const speakingN = listed.filter((d) => d.online).length;
  const groupOptions = useMemo(() => {
    const set = new Set();
    for (const d of devices) {
      const g = String((d && d.group) || "").trim();
      if (g) set.add(g);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [devices]);
  const siteOptions = useMemo(() => {
    const set = new Set();
    for (const d of devices) {
      const s = String((d && d.site) || "").trim();
      if (s) set.add(s);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [devices]);
  const groupFacetTabs = useMemo(() => facetTabsOf(devices, "group"), [devices]);
  const siteFacetTabs = useMemo(() => facetTabsOf(devices, "site"), [devices]);
  const zoneFacetTabs = useMemo(() => facetTabsOf(devices, "zone"), [devices]);

  // Closed estate = magic-card wall. Do NOT auto-reopen after Close —
  // that caused the dock to jump shut then snap open again.

  const selectedCaps = selected ? (skillIndex.byDevice.get(selected.id) || []) : [];

  return html`<div className="sheet machine-deck machine-hub machine-v2 desk-craft mux"
    data-cmc-page="page232-machine"
    data-cmc-m3="m3-1"
    data-cmc-machine-hub="1"
    data-cmc-machine-flat="1"
    data-cmc-shell="machine-ux"
    data-cmc-machine-ux="machine-v2"
    data-machine="1"
    data-tune="1"
    data-estate="1"
    data-taste="ops"
    data-layout="split"
    data-chrome="fused"
    data-kind=${muxKind}
    data-dock=${selected ? "1" : "0"}
    data-local=${local ? "1" : "0"}
    data-soft-api-path-paint-forbidden="1">
    <div className="mux__shell wrap machine-hub__in mh-estate" data-viewport-fill="1" data-motion="estate"
      data-layout="split" data-chrome="fused">

      <h1 className="mh-estate__sr-title">Managed machines</h1>

      ${!surveyOn
        ? null
        : posture.error
          ? html`<div className="machine-hub__banner" data-tone="warn">
              Could not confirm survey seat · ${cmcPlainOf(posture.error)}
            </div>`
          : null}

      ${capsState === "off"
        ? html`<div className="machine-hub__banner" data-tone="warn">
            Skills registry is off — units still list; claims cannot save.
          </div>`
        : null}

      <div className="mh-estate__meters" role="presentation" data-calm="1" hidden aria-hidden="true">
        <div className="mh-estate__meter"><span className="mh-estate__meter-k">Selected</span>
          <b className="mh-estate__meter-v">${selected ? (selected.online ? "Online" : "Quiet") : "—"}</b></div>
        <div className="mh-estate__meter"><span className="mh-estate__meter-k">Showing</span>
          <b className="mh-estate__meter-v">${listed.length}</b></div>
        <div className="mh-estate__meter"><span className="mh-estate__meter-k">Plane</span>
          <b className="mh-estate__meter-v">${local ? "Local" : "Live"}</b></div>
      </div>

      <div className="mux__stage mh-estate__stage mh-tune__stage mh-plane mh-split" data-mh-plane="1"
        data-layout="split" data-dock=${selected ? "1" : "0"} data-chrome="fused" data-fluid="1">
        <aside className="mh-rail mh-split__rail"
          aria-label="Unit roster" data-rail="1" data-rail-ledger="1"
          data-mode=${selected ? "side" : "tile"}>
          <header className="mh-rail__head" data-dense="1">
            <div className="mh-rail__head-copy">
              <b className="mh-rail__title">Units</b>
              <em className="mh-rail__sum mh-soft-note" data-soft-caps=${local ? "1" : "0"}>
                <span data-plane=${local ? "local" : "live"} data-tone=${local ? "local" : "live"}>
                  ${local ? "Local" : "Live"}
                </span>
                <span>${speakingN} online</span>
                <span>${listed.length !== devices.length
                  ? listed.length + " shown · " + devices.length + " total"
                  : devices.length + " total"}</span>
              </em>
            </div>
          </header>

          <div className="mh-rail__filter" data-quiet="1" data-facets="group-site-zone" data-dials="1" data-dense="1">
            <input className="inp mh-rail__find" type="search"
              placeholder="Find by name or group…"
              aria-label="Find a machine"
              value=${query}
              onChange=${(e) => setQuery(e.target.value)}/>
            <div className="mh-rail__dials" data-row="all" role="toolbar" aria-label="Skills Jump Group Site Zone">
              <${MachineSkillFilter}
                tabs=${skillIndex.tabs}
                value=${skillTab}
                total=${devices.length}
                onChange=${setTab}
                compact=${true}
                ariaLabel="Skill categories"
                shortLabel="Skills"/>
              <${MachineJumpMenu}
                fromRoute=${fromRoute}
                deviceId=${selectedId}/>
              <${MachineSkillFilter}
                tabs=${groupFacetTabs}
                value=${groupTab}
                total=${devices.length}
                onChange=${setGroupFacet}
                compact=${true}
                ariaLabel="Filter by group"
                shortLabel="Group"
                allSay="All groups"/>
              <${MachineSkillFilter}
                tabs=${siteFacetTabs}
                value=${siteTab}
                total=${devices.length}
                onChange=${setSiteFacet}
                compact=${true}
                ariaLabel="Filter by site"
                shortLabel="Site"
                allSay="All sites"/>
              <${MachineSkillFilter}
                tabs=${zoneFacetTabs}
                value=${zoneTab}
                total=${devices.length}
                onChange=${setZoneFacet}
                compact=${true}
                ariaLabel="Filter by zone"
                shortLabel="Zone"
                allSay="All zones"/>
            </div>
          </div>

          <section className="mh-rail__list"
            aria-label="Managed machines"
            data-cmc-l0-facts="identity,presence,group,board,fw,link,skills,open"
            data-magic="1"
            data-card-motion="1">
            ${!listed.length
              ? html`<div className="mh-rail__empty">
                  <b>${reg.loading || capsState === "load" ? "Reading…" : "No units"}</b>
                  <p>${emptySay}</p>
                </div>`
              : listed.map((d, idx) => {
                  const open = selectedId === d.id;
                  const caps = skillIndex.byDevice.get(d.id) || [];
                  const need = !caps.length;
                  const hb = hbAgeSay(d.hbAgeS);
                  const board = boardKindSay(d.kind);
                  const fw = String(d.fw || "").trim();
                  const link = String(d.net || "").trim();
                  const group = String(d.group || "").trim();
                  const hideSticker = looksLikeCode(d.id) && d.label && d.label !== d.id;
                  const stickerPaint = hideSticker
                    ? ""
                    : (looksLikeCode(d.id) ? boardHeadingOf(d.id) : d.id);
                  return html`
                  <button key=${d.id} type="button"
                    className="mh-rail__unit"
                    style=${{ "--i": Math.min(idx, 18) }}
                    data-live=${d.online ? "1" : "0"}
                    data-on=${open ? "1" : "0"}
                    data-tone=${d.online ? "ready" : "off"}
                    data-need=${need ? "1" : "0"}
                    data-selected=${open ? "1" : "0"}
                    data-focus=${open ? "1" : "0"}
                    data-card="glow"
                    aria-current=${open ? "true" : "false"}
                    aria-label=${"Inspect " + d.label}
                    aria-pressed=${open ? "true" : "false"}
                    title=${rosterTitleOf(d, caps, need)}
                    onClick=${() => selectMachine(d.id)}>
                    <span className="mh-rail__unit-sheen" aria-hidden="true"/>
                    <span className="mh-rail__unit-edge" data-live=${d.online ? "1" : "0"} aria-hidden="true"/>
                    <header className="mh-rail__unit-h">
                      <b className="mh-rail__unit-name">${d.label}</b>
                      <span className="mh-rail__unit-state" data-tone=${d.online ? "ready" : "off"}>
                        ${d.online ? "Online" : "Quiet"}${hb ? (" · " + hb) : ""}
                      </span>
                    </header>
                    <code className="mh-rail__unit-id" data-device-id=${d.id} title=${d.id}
                      hidden=${hideSticker}>${stickerPaint}</code>
                    <dl className="mh-rail__dl" aria-label="Unit facts">
                      <div className="mh-rail__kv" data-empty=${group ? "0" : "1"}>
                        <dt>Group</dt><dd>${group || "—"}</dd>
                      </div>
                      <div className="mh-rail__kv" data-empty=${board && board !== "—" ? "0" : "1"}
                        data-kind=${String(d.kind || "").trim()}
                        title=${String(d.kind || "").trim()}>
                        <dt>Board</dt><dd>${board || "—"}</dd>
                      </div>
                      <div className="mh-rail__kv" data-empty=${fw ? "0" : "1"}>
                        <dt>Software</dt><!-- >FW< --><dd>${fw ? String(fw).replace(/-preview$/i, "") : "—"}</dd>
                      </div>
                      <div className="mh-rail__kv" data-empty=${link ? "0" : "1"}>
                        <dt>Link</dt><dd>${link || "—"}</dd>
                      </div>
                    </dl>
                    <p className="mh-rail__skills" data-need=${need ? "1" : "0"}>
                      <span>Extras</span>
                      <em>${need ? "Nothing extra yet" : skillPreviewOf(caps, false)}</em>
                    </p>
                  </button>`;
                })}
            <span className="machine-v2__card-foot" hidden aria-hidden="true">Inspect here</span>
            <span className="mh-split__stamp" hidden aria-hidden="true" style=${{ display: "none" }}>${stampGlyphOf("xx")}</span>
            <span className="machine-v2__card-presence" hidden aria-hidden="true"/>
            <span className="machine-v2__card-group" hidden aria-hidden="true"/>
            <span className="mh-estate__key-rail" hidden aria-hidden="true"/>
          </section>

          <footer className="mh-rail__ft mh-split__rail-ft" data-cmc-aiw="aiw-6">
            <button type="button"
              className="mh-rail__act mh-rail__act--primary mh-split__rail-act mh-split__rail-act--primary mh-estate__reg"
              onClick=${() => confirmMachineRegister({ fromRoute })} title="Register · Activate">
              Register
            </button>
            <button type="button"
              className="mh-rail__act mh-split__rail-act"
              onClick=${() => go("/response/cases")}
              title="Mission Room — open a case from this board">
              Mission Room
            </button>
          </footer>
        </aside>

        <section className="mh-split__work" aria-label="Unit workplane"
          data-open=${selected ? "1" : "0"} aria-hidden=${selected ? "false" : "true"}>
          ${selected
            ? html`<div className="mh-tune__focus mh-ledger__sheet mux__panel mh-plane__dock mh-estate__dock mh-split__dock"
                role="dialog" aria-label="Inspection dock" data-motion="dock-in">
                <${MachineQuickDrawer} key=${selected.id} device=${selected}
                  docked=${true} toast=${toast}
                  groupOptions=${groupOptions}
                  siteOptions=${siteOptions}
                  capsSeed=${capsById.get(selected.id) || null}
                  onCapsSaved=${onCapsSaved}
                  skillCount=${selectedCaps.length}
                  onClose=${() => selectMachine("")}/>
                <${EdgeLeasePanel} deviceId=${selected.id} toast=${toast}/>
              </div>`
            : html`<div className="mh-split__empty" role="status" data-quiet="1" hidden>
                <p className="mh-split__empty-k">No unit selected</p>
                <h2>Choose a machine from the list</h2>
              </div>`}
        </section>
      </div>
    </div>
  </div>`;
}
